import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { LanguageService } from '@educational-initiatives/angular-web-utilities';
import type { AvailableLanguage } from '@app/core';
import type { NavItem, SidebarNavItem } from '@app/shared';
import {
  buildLocaleSwitchUrl,
  DEFAULT_LAYOUT_VALUES,
  FOOTER_AVAILABLE_LOCALES,
  FOOTER_LEGAL_BASE_URL,
} from '@app/shared/constants';
import { navigateWithoutEndingSession } from '@app/core/services/browser-close-logout/browser-close-logout';
import { LayoutIconComponent } from '@app/shared/components';

/**
 * @fileoverview SidebarComponent
 * @description
 * **The Navigation Drawer**
 * A configurable sidebar that renders navigation items with icons, legal links,
 * locale selector, and copyright. Supports overlay (mobile) and push (desktop)
 * modes, with configurable position (left/right).
 *
 * ARCHITECTURAL PRINCIPLES:
 * 1. Layout-level navigation
 * 2. Responsive mode (overlay vs push)
 * 3. Configurable position
 */
@Component({
  selector: 'app-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    RouterLinkActive,
    FormsModule,
    TranslocoModule,
    LayoutIconComponent,
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class SidebarComponent {
  private readonly _languageService = inject(LanguageService);

  /** Whether the sidebar is open */
  public readonly isOpen = input<boolean>(false);

  /** Main navigation items */
  public readonly navItems = input<NavItem[]>([]);

  /** Extended sidebar items (Settings, Help, etc.) */
  public readonly sidebarItems = input<SidebarNavItem[]>([]);

  /** Company name for copyright */
  public readonly companyName = input('');

  /** Logo filename for AssetUrlPipe */
  protected readonly _logoFilename = 'ei_educational_initiatives.png';

  /** Emitted when sidebar should close */
  public readonly close = output<void>();

  protected readonly _displayCompanyName = computed(
    () => this.companyName() || DEFAULT_LAYOUT_VALUES.COMPANY_NAME,
  );

  protected readonly _currentYear = new Date().getFullYear();

  protected readonly _availableLocales = computed(
    () => FOOTER_AVAILABLE_LOCALES,
  );

  protected readonly _currentLocale = computed(() =>
    this._languageService.currentLang(),
  );

  /** All nav items (main + sidebar extra) */
  protected readonly _allNavItems = computed(() => {
    const main = this.navItems();
    const extra = this.sidebarItems();
    return [...main, ...extra];
  });

  protected _onClose(): void {
    this.close.emit();
  }

  protected _onNavigate(): void {
    this.close.emit();
  }

  protected _legalUrl(path: string): string {
    const locale = this._currentLocale();
    const base = FOOTER_LEGAL_BASE_URL.replace(/\/$/, '');
    return `${base}/${locale}/${path}`;
  }

  protected _getLanguageName(lang: AvailableLanguage): string {
    return this._languageService.getLanguageName(lang);
  }

  protected _onLocaleChange(newLocale: string): void {
    const locale = newLocale as AvailableLanguage;
    if (locale === this._currentLocale()) {
      return;
    }
    if (!this._languageService.isValidLanguage(locale)) {
      return;
    }
    const pathname = window.location.pathname;
    const absoluteUrl = buildLocaleSwitchUrl(
      locale,
      pathname,
      window.location.origin,
    );
    navigateWithoutEndingSession(absoluteUrl);
  }

  /** Resolve full path for routerLink (paths like 'payments' need /home prefix) */
  protected _resolvePath(path: string): string {
    if (path.startsWith('/') || path === '#' || path.startsWith('http')) {
      return path;
    }
    return `/home/${path}`;
  }

  protected _isExternalLink(path: string): boolean {
    return path === '#' || path.startsWith('http');
  }
}
